import React, { useState, useEffect } from 'react'
import { PartnerService } from '@duck/api-definitions'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@duck/ui'
import { OrganizationForm } from './OrganizationForm'
import { apis } from '@modules/apiClients'
import { useTar } from '@/modules'

interface OrganizationDialogProps {
    isOpen: boolean
    onClose: () => void
    organization: PartnerService.Organization | null
    onOrganizationSaved?: () => void
}

export const OrganizationDialog: React.FC<OrganizationDialogProps> = ({
    isOpen,
    onClose,
    organization,
    onOrganizationSaved,
}) => {
    const { t } = useTar()
    const [formData, setFormData] = useState<Partial<PartnerService.Organization>>({})
    const [isSaving, setIsSaving] = useState(false)
    const [saveError, setSaveError] = useState<string | null>(null)

    // Reset form data when organization changes
    useEffect(() => {
        if (organization) {
            setFormData({ ...organization })
        } else {
            setFormData({
                name: '',
                description: '',
                type: PartnerService.OrganizationType.Company,
                active: true,
                logoURL: '',
            })
        }
        setSaveError(null)
        setIsSaving(false)
    }, [organization])

    const handleInputChange = (field: keyof PartnerService.Organization, value: string | boolean | PartnerService.OrganizationType | string[]) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }))
    }

    const handleSave = async () => {
        setSaveError(null)

        // Basic validation
        if (!formData.name || !formData.type) {
            setSaveError('Name and type are required.')
            return
        }

        try {
            setIsSaving(true)
            if (organization) {
                // Update existing organization
                await apis.partnerService.organizationsOrganizationIdPut(
                    organization.id,
                    formData as PartnerService.UpdateOrganizationRequest
                )
            } else {
                // Create new organization
                await apis.partnerService.organizationsPost(formData as PartnerService.CreateOrganizationRequest)
            }

            // Call the callback to refresh parent component
            onOrganizationSaved?.()
            onClose()
        } catch (error: unknown) {
            console.error('Failed to save organization:', error)
            const errorMessage = (error as { response?: { data?: { error?: string } } })?.response?.data?.error
                || 'Failed to save organization. Please try again.'
            setSaveError(errorMessage)
        } finally {
            setIsSaving(false)
        }
    }

    return (
        <Dialog
            open={isOpen}
            onOpenChange={(open) => !isSaving && !open && onClose()}
        >
            <DialogContent
                className="sm:max-w-3xl lg:max-w-4xl sm:min-w-[500px] lg:min-w-[600px] max-h-[90vh] overflow-y-auto"
                onPointerDownOutside={(e) => isSaving && e.preventDefault()}
                onEscapeKeyDown={(e) => isSaving && e.preventDefault()}
                onInteractOutside={(e) => isSaving && e.preventDefault()}
            >
                <DialogHeader>
                    <DialogTitle>
                        {organization ? `${t('organizations.editOrganization')}: ${organization.name}` : t('organizations.newOrganization')}
                    </DialogTitle>
                </DialogHeader>

                <OrganizationForm
                    formData={formData}
                    onChange={handleInputChange}
                    onSave={handleSave}
                    onCancel={onClose}
                    isSaving={isSaving}
                    saveError={saveError}
                    isEditing={!!organization}
                    isReadOnly={false}
                />
            </DialogContent>
        </Dialog>
    )
}

